import { atom } from "jotai";
import {
    activeFinanceMetricsAtomBS,
    activeFinLabMetricsAtomBS,
    balanceSheetResolutionAtom,
    filterMetricsByLabels,
    financeMonthlyQueryAtomBS,
    financeQuarterlyQueryAtomBS,
    financeYearlyQueryAtomBS,
    financialHighlightsAsOfDateAtom,
    finLabMonthlyQueryAtomBS,
    finLabQuarterlyQueryAtomBS,
    finLabYearlyQueryAtomBS,
} from "../../../metrics.state";
import { FinanceLabField, formatFinanceLabValue } from "@/entities/FinanceLab";
import { getMetric } from "../../../utils/getMetric";
import {
    getAllAvailablePeriods,
    getPeriodColumns,
    getPeriodsForWindow,
    getUniquePeriods,
    getYTDPeriods,
    sortPeriods,
} from "../../../utils/periods";
import { HeaderWithTooltip } from "../../../components/HeaderWithTooltip";
import { rowConfig } from "./rowConfig";
import { calculateDelta, getDeltaPeriodCount } from "../../../utils/deltas";
import { ColDef } from "ag-grid-enterprise";
import { DeltaCellRenderer } from "../DeltaCellRenderer";
import { RowConfigWithMetric, SummaryRow } from "../SummaryFinancials.types";
import { BALANCE_SHEET_LABELS } from "../../../metrics.const";

export const BSSummaryMetricsAtom = atom<RowConfigWithMetric[]>((get) => {
    const financeMetrics = get(activeFinanceMetricsAtomBS);
    const finLabMetrics = get(activeFinLabMetricsAtomBS);

    const unified = {
        ...finLabMetrics,
        ...financeMetrics,
    };

    const labels = Array.from(BALANCE_SHEET_LABELS);
    const metrics = filterMetricsByLabels(unified, labels);

    return rowConfig.map((row) => {
        return {
            metric: getMetric(
                metrics,
                row.codes[0] as string,
                row.codes[1] as FinanceLabField,
            ),
            ...row,
        };
    });
});

export const BSSummaryPeriodsAtom = atom<string[]>((get) => {
    const metricResolution = get(balanceSheetResolutionAtom);
    const tablePeriodCount = getDeltaPeriodCount(metricResolution.window || "");
    const asOfDate = get(financialHighlightsAsOfDateAtom);
    console.log("metric resolution", metricResolution);

    if (!asOfDate) {
        return [];
    }

    if (!["all", "ytd"].includes(metricResolution.window || "")) {
        return sortPeriods(
            getPeriodsForWindow(
                asOfDate,
                metricResolution.periodKind,
                tablePeriodCount,
            ),
        );
    }

    const metrics = get(BSSummaryMetricsAtom);
    const periods = getAllAvailablePeriods(
        getUniquePeriods(
            metrics.map((m) => m.metric).filter((m) => m !== undefined),
        ),
        asOfDate,
    );

    if (
        metricResolution.periodKind === "yearly" &&
        metricResolution.window === "all"
    ) {
        periods.pop();
    }

    return metricResolution.window?.includes("ytd")
        ? getYTDPeriods(periods, asOfDate)
        : periods;
});

export interface PeriodColumn {
    field: string;
    headerName: string;
    headerComponent?: any;
    headerComponentParams?: any;
}

// Column definitions for the balance sheet summary table
export const BSSummaryColumnAtom = atom<ColDef[]>((get) => {
    const periods = get(BSSummaryPeriodsAtom);
    const periodColumns = getPeriodColumns(periods);

    return [
        {
            field: "label",
            headerName: "",
            flex: 1.5,
            pinned: "left",
            cellClass: "heading-s",
        },
        ...periodColumns,
        {
            field: "popChange",
            maxWidth: 150,
            pinned: "right",
            headerName:
                periodColumns.length >= 2
                    ? `% Δ ${periodColumns[periodColumns.length - 2].headerName} — ${periodColumns[periodColumns.length - 1].headerName}`
                    : "% Δ",
            headerComponent: HeaderWithTooltip,
            cellRenderer: DeltaCellRenderer,
            headerComponentParams: {
                tooltipText:
                    "Shows the % difference between the latest two available periods. Eg. if the last closed period is Q1-25, the delta shows the difference between Q1-25 and Q4-24.",
            },
        },
    ];
});

export const BSSummaryDataAtom = atom<SummaryRow[]>((get) => {
    const rows = get(BSSummaryMetricsAtom);
    const periods = get(BSSummaryPeriodsAtom);
    console.log(periods, rows);
    return rows.map((r) => {
        const row: SummaryRow = { ...r, previousSlice: [], currentSlice: [] };
        if (!r.metric) {
            periods.forEach((period) => {
                row[period] = "-";
            });
            row.popChange = null;
            return row;
        }

        const valuesByPeriod = new Map(
            periods.map((p) => [
                p,
                r.metric?.values.filter((value) => value.period === p).pop()
                    ?.value,
            ]),
        );
        const arr = Array.from(valuesByPeriod.entries());
        const currentSlice = arr.pop();
        const previousSlice = arr.pop();
        const slices = {
            previous: [previousSlice].filter(
                (s) => Array.isArray(s) && typeof s[1] === "number",
            ) as [string, number][],
            current: [currentSlice].filter(
                (s) => Array.isArray(s) && typeof s[1] === "number",
            ) as [string, number][],
        };
        const delta = calculateDelta(
            slices?.current || [],
            slices?.previous || [],
        );

        if (delta !== null) {
            row.popChange = delta;
            row.previousSlice = slices.previous;
            row.currentSlice = slices.current;
        }

        periods.forEach((period) => {
            row[period] =
                valuesByPeriod.get(period) !== undefined
                    ? formatFinanceLabValue(
                          valuesByPeriod.get(period),
                          row.fieldType,
                      )
                    : "-";
        });

        return row;
    });
});

export const isLoadingAtom = atom<boolean>((get) => {
    const balanceSheetResolution = get(balanceSheetResolutionAtom);
    const bsFinLabQueryAtom =
        balanceSheetResolution.periodKind === "quarterly"
            ? finLabQuarterlyQueryAtomBS
            : balanceSheetResolution.periodKind === "yearly"
              ? finLabYearlyQueryAtomBS
              : finLabMonthlyQueryAtomBS;

    const bsFinanceQueryAtom =
        balanceSheetResolution.periodKind === "quarterly"
            ? financeQuarterlyQueryAtomBS
            : balanceSheetResolution.periodKind === "yearly"
              ? financeYearlyQueryAtomBS
              : financeMonthlyQueryAtomBS;

    return (
        get(bsFinLabQueryAtom).isLoading || get(bsFinanceQueryAtom).isLoading
    );
});
